import PropTypes from "prop-types"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardBody, Col, Container, Row, Badge, Spinner } from "reactstrap"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts"

import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { useAuth } from "hooks/authHook"
import { useAccountDashboard } from "hooks/useAccountDashboard"
import { ACCESS } from "constants/accessPermissions"
import { formatCurrency } from "utils/formatCurrency"
import AuctionSheetExtractModal from "pages/StockPages/StockView/AuctionSheetExtractModal"

const StatCard = ({ title, value, sub, color, icon }) => (
  <Card className="border-0 shadow-sm h-100">
    <CardBody className="d-flex align-items-center gap-3 p-3">
      <div className={`avatar-sm rounded d-flex align-items-center justify-content-center bg-${color} bg-opacity-10 flex-shrink-0`}>
        <i className={`${icon} text-${color} font-size-20`} />
      </div>
      <div className="overflow-hidden">
        <p className="text-muted mb-1 small fw-semibold text-truncate">{title}</p>
        <h5 className="mb-0 fw-bold">{value}</h5>
        {sub && <p className="text-muted mb-0 small">{sub}</p>}
      </div>
    </CardBody>
  </Card>
)

const QuickLink = ({ to, icon, label, color }) => (
  <Link to={to} className="text-decoration-none">
    <Card className="border-0 shadow-sm text-center py-3 h-100" style={{ cursor: "pointer" }}>
      <CardBody className="p-2">
        <div className={`avatar-sm rounded mx-auto mb-2 d-flex align-items-center justify-content-center bg-${color} bg-opacity-10`}>
          <i className={`${icon} text-${color} font-size-20`} />
        </div>
        <p className="mb-0 small fw-semibold text-dark">{label}</p>
      </CardBody>
    </Card>
  </Link>
)

const QuickAction = ({ onClick, icon, label, color }) => (
  <div onClick={onClick} role="button" style={{ cursor: "pointer" }} className="h-100">
    <Card className="border-0 shadow-sm text-center py-3 h-100">
      <CardBody className="p-2">
        <div className={`avatar-sm rounded mx-auto mb-2 d-flex align-items-center justify-content-center bg-${color} bg-opacity-10`}>
          <i className={`${icon} text-${color} font-size-20`} />
        </div>
        <p className="mb-0 small fw-semibold text-dark">{label}</p>
      </CardBody>
    </Card>
  </div>
)

const Dashboard = props => {
  document.title = "Dashboard | Aurobil Cars"
  const { user, hasPermission, hasAnyPermission } = useAuth()
  const [auctionSheetOpen, setAuctionSheetOpen] = useState(false)

  const canViewDashboardPanel = hasPermission(ACCESS.DASHBOARD.VIEW_DASHBOARD_PANEL)
  const canViewAccountBalances = hasPermission(ACCESS.DASHBOARD.VIEW_ACCOUNT_BALANCE)
  const canViewMonthlyIncome = hasPermission(ACCESS.DASHBOARD.VIEW_MONTHLY_INCOME)
  const canViewMonthlyExpenses = hasPermission(ACCESS.DASHBOARD.VIEW_MONTHLY_EXPENSE)
  const canViewLoans = hasPermission(ACCESS.DASHBOARD.VIEW_OUTSTANDING_LOAN)
  const canViewTrendChart = hasPermission(ACCESS.DASHBOARD.VIEW_INCOME_EXPENSE_TREND)

  const canViewFinanceSection =
    canViewDashboardPanel &&
    (canViewAccountBalances ||
      canViewMonthlyIncome ||
      canViewMonthlyExpenses ||
      canViewLoans ||
      canViewTrendChart)

  const canCreateStock =
    hasPermission(ACCESS.STOCK.CREATE) && hasPermission(ACCESS.STOCK.VIEW_STOCK_LIST)

  const { data: response, isLoading } = useAccountDashboard(canViewFinanceSection)
  const dash = response?.data

  const totalBalance     = dash?.total_balance ?? 0
  const monthlyIncome    = dash?.monthly_income ?? 0
  const monthlyExpenses  = dash?.monthly_expenses ?? 0
  const outstandingLoans = dash?.outstanding_loans ?? { count: 0, total: 0 }
  const monthlyTrend     = dash?.monthly_trend ?? []
  const accountBalances  = dash?.account_balances ?? []

  const quickLinks = [
    {
      to: "/employee-list",
      icon: "mdi mdi-account-tie-outline",
      label: "Employee Management",
      color: "primary",
      visible:
        hasPermission(ACCESS.EMPLOYEE_MANAGEMENT.VIEW_PANEL) &&
        hasPermission(ACCESS.EMPLOYEE.VIEW_LIST),
    },
    {
      to: "/showroom-list",
      icon: "mdi mdi-storefront-outline",
      label: "Showroom",
      color: "info",
      visible:
        hasPermission(ACCESS.SHOWROOM_PANEL.VIEW_PANEL) &&
        hasPermission(ACCESS.SHOWROOM.VIEW_SHOWROOM_LIST),
    },
    {
      to: "/user-list",
      icon: "mdi mdi-account-cog-outline",
      label: "User Management",
      color: "secondary",
      visible:
        hasPermission(ACCESS.USER_PANEL.VIEW_PANEL) &&
        hasPermission(ACCESS.USER.VIEW_USER_LIST),
    },
    {
      to: "/stock-list",
      icon: "mdi mdi-car",
      label: "Vehicle Management",
      color: "primary",
      visible:
        hasPermission(ACCESS.STOCK_PANEL.VIEW_PANEL) &&
        hasPermission(ACCESS.STOCK.VIEW_STOCK_LIST),
    },
    {
      to: "/vehicle-movement-list?tab=movement-list",
      icon: "mdi mdi-swap-horizontal-bold",
      label: "Vehicle Movement",
      color: "warning",
      visible:
        hasPermission(ACCESS.VEHICLE_MOVEMENT_PANEL.VIEW) &&
        hasPermission(ACCESS.VEHICLE_MOVEMENT.VIEW_LIST),
    },
    {
      to: "/fuel-usage-list",
      icon: "mdi mdi-gas-station-outline",
      label: "Vehicle Fuel",
      color: "danger",
      visible:
        hasPermission(ACCESS.VEHICLE_FUEL_PANEL.VIEW) &&
        hasPermission(ACCESS.VEHICLE_FUEL_USE.VIEW_LIST),
    },
    {
      to: "/vehicle-content-list",
      icon: "mdi mdi-note-text-outline",
      label: "Content Manager",
      color: "secondary",
      visible:
        hasPermission(ACCESS.CONTENT_MANAGER.VIEW_CONTENT_MANAGER) &&
        hasPermission(ACCESS.CONTENT_MANAGER.VIEW_VEHICLE_CONTENT_LIST),
    },
    {
      to: "/customer-list",
      icon: "mdi mdi-account-group",
      label: "Customer",
      color: "info",
      visible:
        hasPermission(ACCESS.CUSTOMER_PANEL.VIEW) &&
        hasPermission(ACCESS.CUSTOMER.VIEW_LIST),
    },
    {
      to: "/money-receipts",
      icon: "mdi mdi-receipt",
      label: "Money Receipt",
      color: "success",
      visible:
        hasPermission(ACCESS.MONEY_RECEIPT_PANEL.VIEW) &&
        hasPermission(ACCESS.MONEY_RECEIPT.VIEW_LIST),
    },
    {
      to: "/quotation-list",
      icon: "mdi mdi-file-document-outline",
      label: "Quotation",
      color: "info",
      visible: hasPermission(ACCESS.CUSTOMER.VIEW_LIST),
    },
    {
      to: "/accessories-list",
      icon: "mdi mdi-package-variant-closed",
      label: "Accessories",
      color: "dark",
      visible:
        hasPermission(ACCESS.ACCESSORIES_MANAGEMENT.VIEW) &&
        hasPermission(ACCESS.ACCESSORIES.VIEW_LIST),
    },
    {
      to: "/petty-cash-transaction-list",
      icon: "mdi mdi-cash-multiple",
      label: "Petty Cash",
      color: "success",
      visible: hasAnyPermission([
        ACCESS.PETTY_CASH_PAYMENT_FROM.VIEW_LIST,
        ACCESS.PETTY_CASH_PAYMENT_MODE.VIEW_LIST,
        ACCESS.PETTY_CASH_PAYMENT_PURPOSE.VIEW_LIST,
        ACCESS.PETTY_CASH_TRANSACTION.VIEW_LIST,
        ACCESS.PETTY_CASH_REPORT.VIEW_LIST,
      ]),
    },
    {
      to: "/account-workspace",
      icon: "mdi mdi-wallet-outline",
      label: "Account Management",
      color: "primary",
      visible:
        hasPermission(ACCESS.ACCOUNT_MANAGEMENT.VIEW) &&
        hasAnyPermission([
          ACCESS.ACCOUNT_MANAGEMENT.VIEW,
          ACCESS.ACCOUNT_VENDOR_BILL.VIEW_LIST,
          ACCESS.ACCOUNT_VENDOR_PAYMENT.VIEW_LIST,
          ACCESS.ACCOUNT_CUSTOMER_PAYMENT.VIEW_LIST,
          ACCESS.ACCOUNT_LOAN.VIEW_LIST,
          ACCESS.ACCOUNT_LOAN_REPAYMENT.VIEW_LIST,
          ACCESS.ACCOUNT_TRANSACTION.VIEW_LIST,
          ACCESS.ACCOUNT_EXPENSE.VIEW_LIST,
          ACCESS.ACCOUNT_FUND_TRANSFER.VIEW_LIST,
          ACCESS.ACCOUNT.VIEW_LIST,
        ]),
    },
    {
      to: "/account-rtgs-payment-list",
      icon: "mdi mdi-bank-transfer",
      label: "RTGS Payments",
      color: "danger",
      visible: hasPermission(ACCESS.ACCOUNT_RTGS_PAYMENT.VIEW_LIST),
    },
    {
      to: "/account-duty-payment-list",
      icon: "mdi mdi-file-document-edit-outline",
      label: "Duty Payments",
      color: "primary",
      visible: hasPermission(ACCESS.ACCOUNT_DUTY_PAYMENT.VIEW_LIST),
    },
    {
      to: "/importer-list",
      icon: "mdi mdi-store-outline",
      label: "Importer List",
      color: "warning",
      visible: hasPermission(ACCESS.VEHICLE_IMPORTER.VIEW_VEHICLE_IMPORTER_LIST),
    },
    {
      to: "/message-list",
      icon: "mdi mdi-email-outline",
      label: "Message List",
      color: "info",
      visible: hasPermission(ACCESS.MESSAGE.VIEW_CONTACT_MESSAGE_LIST),
    },
  ].filter(link => link.visible)

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={props.t("Dashboards")} breadcrumbItem={props.t("Dashboard")} />

        {user?.name && (
          <p className="mb-4 text-muted">
            Welcome back, <strong className="text-dark">{user.name}</strong>
          </p>
        )}

        {/* Financial Summary */}
        {canViewFinanceSection && (
          isLoading ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : (
            <>
              <Row className="g-3 mb-4">
                {canViewAccountBalances && (
                  <Col xs={12} sm={6} xl={3}>
                    <StatCard title="Total Balance" value={formatCurrency(totalBalance, "BDT")} color="primary" icon="mdi mdi-bank-outline" />
                  </Col>
                )}
                {canViewMonthlyIncome && (
                  <Col xs={12} sm={6} xl={3}>
                    <StatCard title="Income This Month" value={formatCurrency(monthlyIncome, "BDT")} color="success" icon="mdi mdi-trending-up" />
                  </Col>
                )}
                {canViewMonthlyExpenses && (
                  <Col xs={12} sm={6} xl={3}>
                    <StatCard title="Expenses This Month" value={formatCurrency(monthlyExpenses, "BDT")} color="danger" icon="mdi mdi-trending-down" />
                  </Col>
                )}
                {canViewLoans && (
                  <Col xs={12} sm={6} xl={3}>
                    <StatCard
                      title="Outstanding Loans"
                      value={formatCurrency(outstandingLoans.total, "BDT")}
                      sub={`${outstandingLoans.count} active loan${outstandingLoans.count !== 1 ? "s" : ""}`}
                      color="warning"
                      icon="mdi mdi-cash-clock"
                    />
                  </Col>
                )}
              </Row>

              <Row className="g-3 mb-4">
                {canViewTrendChart && (
                  <Col xs={12} lg={canViewAccountBalances ? 8 : 12}>
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody>
                        <h6 className="fw-semibold mb-3">Monthly Income vs Expenses</h6>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={monthlyTrend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatCurrency(v, "BDT")} width={90} />
                            <Tooltip formatter={value => formatCurrency(value, "BDT")} />
                            <Legend />
                            <Bar dataKey="income" name="Income" fill="#34c38f" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="expenses" name="Expenses" fill="#f46a6a" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardBody>
                    </Card>
                  </Col>
                )}
                {canViewAccountBalances && (
                  <Col xs={12} lg={canViewTrendChart ? 4 : 12}>
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody>
                        <h6 className="fw-semibold mb-3">Account Balances</h6>
                        {accountBalances.length === 0 ? (
                          <p className="text-muted small">No accounts found.</p>
                        ) : (
                          accountBalances.map(acc => (
                            <div key={acc.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                              <span className="small fw-semibold">{acc.name}</span>
                              <Badge color={acc.balance < 0 ? "danger" : "success"} className="font-monospace">
                                {formatCurrency(acc.balance, acc.currency || "BDT")}
                              </Badge>
                            </div>
                          ))
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            </>
          )
        )}

        {/* Quick Navigation */}
        {!!quickLinks.length && (
          <>
            <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.05em" }}>Quick Access</h6>
            <Row className="g-3">
              {canCreateStock && (
                <Col xs={6} sm={4} md={3} lg={2}>
                  <QuickAction
                    onClick={() => setAuctionSheetOpen(true)}
                    icon="mdi mdi-car-cog"
                    label="Add Vehicle from Auction Sheet"
                    color="success"
                  />
                </Col>
              )}
              {quickLinks.map(link => (
                <Col key={link.to} xs={6} sm={4} md={3} lg={2}>
                  <QuickLink to={link.to} icon={link.icon} label={link.label} color={link.color} />
                </Col>
              ))}
            </Row>
          </>
        )}
      </Container>

      <AuctionSheetExtractModal
        isOpen={auctionSheetOpen}
        toggle={() => setAuctionSheetOpen(prev => !prev)}
      />
    </div>
  )
}

Dashboard.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(Dashboard)
